import { useEffect, useRef, useCallback, useState } from "react";
import { useWebSocketNotifications } from "./useWebSocket";

/**
 * Tracks new admin events (TFT payments, password resets, etc.) received via
 * WebSocket and exposes per-tab badge counts + a beep on incoming events.
 *
 * The caller signals which tab is currently active so the hook can auto-clear
 * its corresponding counter (no need to manually mark notifications as read).
 */
const TAB_EVENT_MAP = {
  "tft-payments": ["new_tft_payment_request"],
  "password-resets": ["new_password_reset"],
};

const ALL_TRACKED = Object.values(TAB_EVENT_MAP).flat();

function playBeep() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.type = "sine";
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.setValueAtTime(660, ctx.currentTime + 0.12);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
    o.start();
    o.stop(ctx.currentTime + 0.4);
  } catch { /* noop */ }
}

export function useAdminLiveAlerts(userId, currentTab) {
  const { notifications } = useWebSocketNotifications(userId);
  const seenIds = useRef(new Set());
  const [counts, setCounts] = useState({}); // {tabKey: number}

  // Detect new tracked events
  useEffect(() => {
    if (!notifications.length) return;
    let beeped = false;
    notifications.forEach((n) => {
      const key = n.id || `${n.type}-${n.timestamp || ""}-${n.message || ""}`;
      if (seenIds.current.has(key)) return;
      seenIds.current.add(key);
      if (!ALL_TRACKED.includes(n.type)) return;
      // Find target tab
      const tabKey = Object.keys(TAB_EVENT_MAP).find((t) => TAB_EVENT_MAP[t].includes(n.type));
      if (!tabKey) return;
      if (tabKey === currentTab) return; // user is already viewing the tab
      setCounts((prev) => ({ ...prev, [tabKey]: (prev[tabKey] || 0) + 1 }));
      if (!beeped) { playBeep(); beeped = true; }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications.length, currentTab]);

  // Reset count when user navigates to the tab
  useEffect(() => {
    if (counts[currentTab]) {
      setCounts((prev) => ({ ...prev, [currentTab]: 0 }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  const clear = useCallback((tabKey) => {
    setCounts((prev) => ({ ...prev, [tabKey]: 0 }));
  }, []);

  return { counts, clear };
}
